import * as THREE from 'three';
import { buildBoundingFloorPolygon } from './floorPlanEditorGeometry.js';
import {
  buildDynamicWallRenderModel,
  buildWallSegmentGeometry,
} from './floorPlanDesignerDynamicWallEnds.js';
import { designerGraphFromAnalysisJson } from './floorPlanDesignerPivotWallPolygons.js';
import {
  complementIntervalsMeters,
  DEFAULT_DOOR_WIDTH_METERS,
  DEFAULT_WINDOW_WIDTH_METERS,
  footprintCornersToWorldXZ,
  mergeIntervalsMeters,
  openingHorizontalRangeMeters,
  openingSpanOnWall,
  pointOnWallAtT,
  resolveOpeningT,
  SEGMENT_EPS_METERS,
  wallFootprintCorners2D,
} from './floorPlanWallGeometry.js';
import { buildOpeningMeshesFromRecords, createSceneObjectFromData } from './floorPlanSceneObjects3D.js';
import {
  createArchitecturalWallMaterial,
  createFloorPlanMaterials,
  createRoomFloorMaterial,
  disposeFloorPlanMaterials,
  resolveWallShadeVariantFor3D,
} from './floorPlanThreeTextures.js';
import { getWallMaterialConfig, getRoomFloorMaterialConfig } from './floorPlanMaterialConfig.js';
import { resolveFloorMaterial } from './floorPlanMaterialResolver.js';
import {
  createRoomCeilingMaterial,
  orientCeilingGeometryForInteriorView,
} from './floorPlanCeilingGeometry.js';
import {
  assignFloorWorldUVs,
  buildCenterlineWallSegmentMeshes,
  buildRoomPolygonsForClassification,
  buildTexturedWallPrismMeshes,
} from './floorPlanThreeWallFaces.js';
import {
  attachMeshEdges,
  resolveWallEdgeOptions,
} from './floorPlanThreeVerticalEdges.js';

export const WALL_HEIGHT = 2.8;
export const WALL_THICKNESS = 0.15;
/** Default wall thickness for manual floor plan designer (meters). */
export const MANUAL_WALL_THICKNESS_DEFAULT = 0.1;
/** Dev flag: show wall endpoint spheres in manual designer 3D preview. */
export const DEBUG_MANUAL_3D = false;
export const DOOR_HEIGHT = 2.1;
export const WINDOW_SILL_HEIGHT = 0.9;
export const WINDOW_HEIGHT = 1.2;
export const FALLBACK_METERS_PER_PIXEL = 0.02;
export const NEAREST_WALL_THRESHOLD_PX = 20;
const MIN_POLYGON_AREA_PX2 = 1e-4;

/**
 * @param {Array<Record<string, unknown>>} walls
 * @param {Array<Record<string, unknown>>} doors
 * @param {Array<Record<string, unknown>>} windows
 */
function filterVisible3DWallsAndOpenings(walls, doors, windows) {
  const visibleWalls = walls.filter((wall) => (wall.visibleIn3D ?? true) !== false);
  const visibleWallIds = new Set(visibleWalls.map((wall) => String(wall.id)));
  return {
    visibleWalls,
    doors: doors.filter((door) => visibleWallIds.has(String(door.wallId))),
    windows: windows.filter((window) => visibleWallIds.has(String(window.wallId))),
    visibleWallIds,
  };
}

/**
 * @param {Array<[number, number]>} points
 * @returns {number}
 */
export function polygonSignedArea2D(points) {
  if (!Array.isArray(points) || points.length < 3) return 0;
  let area = 0;
  for (let i = 0; i < points.length; i += 1) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[(i + 1) % points.length];
    area += x1 * y2 - x2 * y1;
  }
  return area / 2;
}

/**
 * Reference winding for wall quads from wallFootprintCorners2D (image Y-down).
 * @returns {number}
 */
function referenceWallFootprintSign() {
  const ref = wallFootprintCorners2D([0, 0], [100, 0], 5);
  return Math.sign(polygonSignedArea2D(ref)) || -1;
}

const MANUAL_WALL_FOOTPRINT_SIGN = referenceWallFootprintSign();

/**
 * @param {Array<[number, number]>} points
 * @param {number} [minArea]
 * @returns {boolean}
 */
export function isValidSimplePolygon(points, minArea = MIN_POLYGON_AREA_PX2) {
  if (!Array.isArray(points) || points.length < 3) return false;
  for (const pt of points) {
    if (!parseImagePoint(pt)) return false;
  }
  return Math.abs(polygonSignedArea2D(points)) > minArea;
}

/**
 * @param {Array<[number, number]>} corners
 * @param {number} [expectedSign]
 * @returns {Array<[number, number]>}
 */
export function ensureConsistentQuadWinding(corners, expectedSign = MANUAL_WALL_FOOTPRINT_SIGN) {
  if (!corners || corners.length !== 4) return corners;
  const sign = Math.sign(polygonSignedArea2D(corners)) || expectedSign;
  if (sign !== expectedSign) {
    return [corners[0], corners[3], corners[2], corners[1]];
  }
  return corners;
}

/**
 * @param {Array<[number, number]>} polygon
 * @returns {Array<[number, number]>}
 */
export function ensureConsistentFloorWinding(polygon) {
  if (!polygon || polygon.length < 3) return polygon;
  const sign = Math.sign(polygonSignedArea2D(polygon)) || 1;
  if (sign < 0) {
    return [...polygon].reverse();
  }
  return polygon;
}

/**
 * @param {Array<{ x: number; z: number }>} corners
 * @param {number} [minArea]
 * @returns {boolean}
 */
function isValidWorldXZQuad(corners, minArea = 1e-8) {
  if (!corners || corners.length !== 4) return false;
  let area = 0;
  for (let i = 0; i < 4; i += 1) {
    const a = corners[i];
    const b = corners[(i + 1) % 4];
    area += a.x * b.z - b.x * a.z;
  }
  return Math.abs(area / 2) > minArea;
}

/**
 * @param {Array<{ x: number; z: number }>} points
 * @returns {number}
 */
function polygonSignedAreaXZ(points) {
  if (!points || points.length < 3) return 0;
  let area = 0;
  for (let i = 0; i < points.length; i += 1) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    area += a.x * b.z - b.x * a.z;
  }
  return area / 2;
}

/**
 * @param {THREE.BufferGeometry} geometry
 * @returns {{ minTopY: number; maxTopY: number; maxTopNormalError: number }}
 */
function manualWallTopDebugMetrics(geometry) {
  const position = geometry.getAttribute('position');
  const normal = geometry.getAttribute('normal');
  let maxY = -Infinity;
  for (let i = 0; i < position.count; i += 1) {
    maxY = Math.max(maxY, position.getY(i));
  }

  let minTopY = Infinity;
  let maxTopY = -Infinity;
  let maxTopNormalError = 0;
  for (let i = 0; i < position.count; i += 1) {
    const y = position.getY(i);
    if (Math.abs(y - maxY) > 1e-9) continue;
    minTopY = Math.min(minTopY, y);
    maxTopY = Math.max(maxTopY, y);
    if (normal) {
      const error = Math.hypot(normal.getX(i), normal.getY(i) - 1, normal.getZ(i));
      maxTopNormalError = Math.max(maxTopNormalError, error);
    }
  }

  return { minTopY, maxTopY, maxTopNormalError };
}

/**
 * @param {Record<string, unknown> | null | undefined} analysisJson
 * @returns {{ metersPerPixel: number; usedFallback: boolean; scaleConfidence: string }}
 */
export function getMetersPerPixel(analysisJson) {
  const scale =
    analysisJson?.scale && typeof analysisJson.scale === 'object'
      ? /** @type {Record<string, unknown>} */ (analysisJson.scale)
      : null;

  const metersPerPixel = Number(scale?.metersPerPixel);
  if (Number.isFinite(metersPerPixel) && metersPerPixel > 0) {
    return {
      metersPerPixel,
      usedFallback: false,
      scaleConfidence: typeof scale?.confidence === 'string' ? scale.confidence : 'unknown',
    };
  }

  const pixelsPerMeter = Number(scale?.pixelsPerMeter);
  if (Number.isFinite(pixelsPerMeter) && pixelsPerMeter > 0) {
    return {
      metersPerPixel: 1 / pixelsPerMeter,
      usedFallback: false,
      scaleConfidence: typeof scale?.confidence === 'string' ? scale.confidence : 'unknown',
    };
  }

  return {
    metersPerPixel: FALLBACK_METERS_PER_PIXEL,
    usedFallback: true,
    scaleConfidence: typeof scale?.confidence === 'string' ? scale.confidence : 'low',
  };
}

/**
 * @param {unknown} point
 * @returns {[number, number] | null}
 */
export function parseImagePoint(point) {
  if (!Array.isArray(point) || point.length < 2) return null;
  const x = Number(point[0]);
  const y = Number(point[1]);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return [x, y];
}

/**
 * @param {Array<[number, number]>} polygon
 * @returns {{ x: number; y: number }}
 */
export function getPolygonCenter(polygon) {
  if (!Array.isArray(polygon) || polygon.length === 0) {
    return { x: 0, y: 0 };
  }
  let sumX = 0;
  let sumY = 0;
  let count = 0;
  for (const pt of polygon) {
    const parsed = parseImagePoint(pt);
    if (!parsed) continue;
    sumX += parsed[0];
    sumY += parsed[1];
    count += 1;
  }
  if (count === 0) return { x: 0, y: 0 };
  return { x: sumX / count, y: sumY / count };
}

/**
 * Map image pixels to the XZ floor plane without mirroring diagonal walls.
 * Image: +X right, +Y down → world: +X right, +Z down (same angle as the 2D overlay).
 * Must stay a proper rotation (det +1), not a swap/flip like (x,z)=(-y,-x) which mirrors slopes.
 *
 * @param {[number, number]} point
 * @param {{ x: number; y: number }} center
 * @param {number} metersPerPixel
 * @returns {{ x: number; z: number }}
 */
export function imagePointToWorld(point, center, metersPerPixel) {
  return {
    x: (point[0] - center.x) * metersPerPixel,
    z: (point[1] - center.y) * metersPerPixel,
  };
}

/**
 * @param {unknown} opening
 * @param {[number, number]} wallStart
 * @param {[number, number]} wallEnd
 * @returns {{ startDist: number; endDist: number; wallLength: number } | null}
 */
export function projectOpeningOntoWall(opening, wallStart, wallEnd) {
  if (!Array.isArray(opening) || opening.length < 2) return null;
  const p0 = parseImagePoint(opening[0]);
  const p1 = parseImagePoint(opening[1]);
  if (!p0 || !p1) return null;

  const [ax, ay] = wallStart;
  const [bx, by] = wallEnd;
  const dx = bx - ax;
  const dy = by - ay;
  const wallLength = Math.hypot(dx, dy);
  if (wallLength < 1e-6) return null;

  const ux = dx / wallLength;
  const uy = dy / wallLength;

  const project = (px, py) => {
    const t = (px - ax) * ux + (py - ay) * uy;
    return Math.max(0, Math.min(wallLength, t));
  };

  const d0 = project(p0[0], p0[1]);
  const d1 = project(p1[0], p1[1]);
  return {
    startDist: Math.min(d0, d1),
    endDist: Math.max(d0, d1),
    wallLength,
  };
}

/**
 * @param {[number, number]} wallStart
 * @param {[number, number]} wallEnd
 * @param {[number, number]} point
 * @returns {number}
 */
function perpendicularDistanceToWall(wallStart, wallEnd, point) {
  const [x1, y1] = wallStart;
  const [x2, y2] = wallEnd;
  const [px, py] = point;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-6) return Infinity;
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lenSq));
  const projX = x1 + t * dx;
  const projY = y1 + t * dy;
  return Math.hypot(px - projX, py - projY);
}

/**
 * @param {unknown} opening
 * @returns {[number, number] | null}
 */
function openingMidpoint(opening) {
  if (!Array.isArray(opening) || opening.length < 2) return null;
  const p0 = parseImagePoint(opening[0]);
  const p1 = parseImagePoint(opening[1]);
  if (!p0 || !p1) return null;
  return [(p0[0] + p1[0]) / 2, (p0[1] + p1[1]) / 2];
}

/**
 * @param {Array<Record<string, unknown>>} walls
 * @param {Array<Record<string, unknown>>} doors
 * @param {Array<Record<string, unknown>>} windows
 * @returns {{
 *   doorsByWallId: Map<string, Array<Record<string, unknown>>>;
 *   windowsByWallId: Map<string, Array<Record<string, unknown>>>;
 *   warnings: string[];
 * }}
 */
export function assignOpeningsToWalls(walls, doors, windows) {
  const warnings = [];
  const doorsByWallId = new Map();
  const windowsByWallId = new Map();

  const wallById = new Map();
  walls.forEach((wall, index) => {
    const id = typeof wall.id === 'string' ? wall.id : `wall_${index + 1}`;
    wallById.set(id, { ...wall, id });
  });

  const assignOpening = (opening, type, collection) => {
    if (!opening || typeof opening !== 'object') return;
    const item = /** @type {Record<string, unknown>} */ (opening);
    const openingId = typeof item.id === 'string' ? item.id : `${type}_unknown`;
    let wallId = typeof item.wallId === 'string' && item.wallId ? item.wallId : null;

    if (!wallId || !wallById.has(wallId)) {
      const mid = openingMidpoint(item.opening);
      if (!mid) return;

      let bestWallId = null;
      let bestDist = Infinity;
      for (const [candidateId, wall] of wallById.entries()) {
        const start = parseImagePoint(wall.start);
        const end = parseImagePoint(wall.end);
        if (!start || !end) continue;
        const dist = perpendicularDistanceToWall(start, end, mid);
        if (dist < bestDist) {
          bestDist = dist;
          bestWallId = candidateId;
        }
      }

      if (bestWallId && bestDist <= NEAREST_WALL_THRESHOLD_PX) {
        wallId = bestWallId;
        warnings.push(`${openingId} was assigned to nearest wall ${bestWallId}`);
      } else {
        warnings.push(`${openingId} could not be assigned to a wall`);
        return;
      }
    }

    if (!collection.has(wallId)) collection.set(wallId, []);
    collection.get(wallId).push(item);
  };

  doors.forEach((door) => assignOpening(door, 'door', doorsByWallId));
  windows.forEach((windowItem) => assignOpening(windowItem, 'window', windowsByWallId));

  return { doorsByWallId, windowsByWallId, warnings };
}

/**
 * @param {{ x: number; z: number }} startPointMeters
 * @param {{ x: number; z: number }} endPointMeters
 * @param {number} bottomY
 * @param {number} topY
 * @param {number} thickness
 * @param {THREE.Material} material
 * @returns {THREE.Mesh | null}
 */
export function createWallPiece(startPointMeters, endPointMeters, bottomY, topY, thickness, material) {
  const height = topY - bottomY;
  if (height <= 0.01) return null;

  const dx = endPointMeters.x - startPointMeters.x;
  const dz = endPointMeters.z - startPointMeters.z;
  const length = Math.hypot(dx, dz);
  if (length <= 0.01) return null;

  const geometry = new THREE.BoxGeometry(length, height, thickness);
  const mesh = new THREE.Mesh(geometry, material);

  const midX = (startPointMeters.x + endPointMeters.x) / 2;
  const midZ = (startPointMeters.z + endPointMeters.z) / 2;
  mesh.position.set(midX, bottomY + height / 2, midZ);
  mesh.rotation.y = Math.atan2(dz, dx);

  return mesh;
}

/**
 * @param {number} dist0
 * @param {number} dist1
 * @param {number} wallLength
 * @returns {Array<[number, number]>}
 */
function mergeIntervals(intervals, wallLength) {
  const sorted = intervals
    .map(([a, b]) => [Math.max(0, Math.min(a, b)), Math.min(wallLength, Math.max(a, b))])
    .filter(([a, b]) => b - a > 0.01)
    .sort((left, right) => left[0] - right[0]);

  if (sorted.length === 0) return [];

  const merged = [sorted[0]];
  for (let i = 1; i < sorted.length; i += 1) {
    const last = merged[merged.length - 1];
    const current = sorted[i];
    if (current[0] <= last[1] + 0.01) {
      last[1] = Math.max(last[1], current[1]);
    } else {
      merged.push(current);
    }
  }
  return merged;
}

/**
 * @param {Array<[number, number]>} blocked
 * @param {number} wallLength
 * @returns {Array<[number, number]>}
 */
function complementIntervals(blocked, wallLength) {
  const solid = [];
  let cursor = 0;
  for (const [start, end] of blocked) {
    if (start - cursor > 0.01) solid.push([cursor, start]);
    cursor = Math.max(cursor, end);
  }
  if (wallLength - cursor > 0.01) solid.push([cursor, wallLength]);
  return solid;
}

/**
 * @param {Array<[number, number]>} yBands
 * @param {number} yBottom
 * @param {number} yTop
 * @param {Array<{ startDist: number; endDist: number }>} doorOpenings
 * @param {Array<{ startDist: number; endDist: number }>} windowOpenings
 * @returns {Array<[number, number]>}
 */
function blockedIntervalsForYBand(yBottom, yTop, doorOpenings, windowOpenings) {
  const blocked = [];

  for (const opening of doorOpenings) {
    if (yBottom < DOOR_HEIGHT && yTop > 0) {
      blocked.push([opening.startDist, opening.endDist]);
    }
  }

  for (const opening of windowOpenings) {
    const windowTop = WINDOW_SILL_HEIGHT + WINDOW_HEIGHT;
    if (yBottom < windowTop && yTop > WINDOW_SILL_HEIGHT) {
      blocked.push([opening.startDist, opening.endDist]);
    }
  }

  return blocked;
}

/**
 * @param {{ x: number; z: number }} wallStartWorld
 * @param {{ x: number; z: number }} wallEndWorld
 * @param {number} wallLength
 * @param {number} dist0
 * @param {number} dist1
 * @returns {{ start: { x: number; z: number }; end: { x: number; z: number } }}
 */
function worldPointsAlongWall(wallStartWorld, wallEndWorld, wallLength, dist0, dist1) {
  const t0 = dist0 / wallLength;
  const t1 = dist1 / wallLength;
  return {
    start: {
      x: wallStartWorld.x + (wallEndWorld.x - wallStartWorld.x) * t0,
      z: wallStartWorld.z + (wallEndWorld.z - wallStartWorld.z) * t0,
    },
    end: {
      x: wallStartWorld.x + (wallEndWorld.x - wallStartWorld.x) * t1,
      z: wallStartWorld.z + (wallEndWorld.z - wallStartWorld.z) * t1,
    },
  };
}

/**
 * @param {Array<[number, number]>} polygon
 * @param {{ x: number; y: number }} center
 * @param {number} metersPerPixel
 * @param {THREE.Material} material
 * @returns {THREE.Mesh | null}
 */
export function buildFloorFromPolygon(polygon, center, metersPerPixel, material) {
  if (!Array.isArray(polygon) || polygon.length < 3) return null;

  const shape = new THREE.Shape();
  let started = false;

  for (const pt of polygon) {
    const parsed = parseImagePoint(pt);
    if (!parsed) continue;
    const world = imagePointToWorld(parsed, center, metersPerPixel);
    if (!started) {
      shape.moveTo(world.x, world.z);
      started = true;
    } else {
      shape.lineTo(world.x, world.z);
    }
  }

  if (!started) return null;

  const geometry = new THREE.ShapeGeometry(shape);
  assignFloorWorldUVs(geometry);
  geometry.rotateX(Math.PI / 2);
  geometry.computeVertexNormals();
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.y = 0.01;
  return mesh;
}

/**
 * @param {Record<string, unknown>} room
 * @param {Array<Record<string, unknown>>} walls
 * @param {number} [defaultHeight]
 * @returns {number}
 */
export function resolveRoomCeilingHeightM(room, walls, defaultHeight = WALL_HEIGHT) {
  const roomHeight = Number(room?.wallHeightMeters);
  if (Number.isFinite(roomHeight) && roomHeight > 0) {
    return roomHeight;
  }

  const boundaryIds = new Set(
    (Array.isArray(room?.boundaryWallIds) ? room.boundaryWallIds : []).map((id) => String(id)),
  );
  if (boundaryIds.size > 0) {
    let minHeight = Infinity;
    walls.forEach((wall) => {
      if (!boundaryIds.has(String(wall.id))) return;
      const heightM = Number(wall.heightMeters);
      if (Number.isFinite(heightM) && heightM > 0) {
        minHeight = Math.min(minHeight, heightM);
      }
    });
    if (Number.isFinite(minHeight) && minHeight < Infinity) {
      return minHeight;
    }
  }

  return defaultHeight;
}

/**
 * @param {Array<[number, number]>} polygon
 * @param {{ x: number; y: number }} center
 * @param {number} metersPerPixel
 * @param {THREE.Material} material
 * @param {number} ceilingHeightM
 * @returns {THREE.Mesh | null}
 */
export function buildCeilingFromPolygon(polygon, center, metersPerPixel, material, ceilingHeightM) {
  if (!Array.isArray(polygon) || polygon.length < 3) return null;
  if (!Number.isFinite(ceilingHeightM) || ceilingHeightM <= 0) return null;

  const shape = new THREE.Shape();
  let started = false;

  for (const pt of polygon) {
    const parsed = parseImagePoint(pt);
    if (!parsed) continue;
    const world = imagePointToWorld(parsed, center, metersPerPixel);
    if (!started) {
      shape.moveTo(world.x, world.z);
      started = true;
    } else {
      shape.lineTo(world.x, world.z);
    }
  }

  if (!started) return null;

  const geometry = new THREE.ShapeGeometry(shape);
  orientCeilingGeometryForInteriorView(geometry);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.y = ceilingHeightM;
  mesh.userData.isRoomCeiling = true;
  return mesh;
}

/**
 * @param {Array<Record<string, unknown>>} exportedRooms
 * @param {Array<Record<string, unknown>>} walls
 * @param {{ x: number; y: number }} center
 * @param {number} metersPerPixel
 * @param {THREE.Material} ceilingMaterial
 * @returns {THREE.Group | null}
 */
export function buildRoomCeilingsGroup(
  exportedRooms,
  walls,
  center,
  metersPerPixel,
  ceilingMaterial,
) {
  if (!exportedRooms.length) return null;
  const ceilingGroup = new THREE.Group();
  ceilingGroup.name = 'ceilings';

  for (const room of exportedRooms) {
    if ((room.visibleIn3D ?? true) === false) continue;
    const polygonRaw = Array.isArray(room.polygon) ? room.polygon : [];
    const roomPolygonRaw = polygonRaw.map((pt) => parseImagePoint(pt)).filter(Boolean);
    if (roomPolygonRaw.length < 3) continue;
    const roomPolygon = ensureConsistentFloorWinding(roomPolygonRaw);
    if (!isValidSimplePolygon(roomPolygon)) continue;
    const ceilingHeightM = resolveRoomCeilingHeightM(room, walls);
    const roomCeiling = buildCeilingFromPolygon(
      roomPolygon,
      center,
      metersPerPixel,
      ceilingMaterial,
      ceilingHeightM,
    );
    if (roomCeiling) {
      ceilingGroup.add(roomCeiling);
    }
  }

  return ceilingGroup.children.length > 0 ? ceilingGroup : null;
}

/**
 * @param {Array<Record<string, unknown>>} walls
 * @param {Map<string, Array<Record<string, unknown>>>} doorsByWallId
 * @param {Map<string, Array<Record<string, unknown>>>} windowsByWallId
 * @param {{ x: number; y: number }} center
 * @param {number} metersPerPixel
 * @param {import('./floorPlanThreeTextures.js').FloorPlanMaterialSet} materials
 * @param {Array<Array<[number, number]>>} roomPolygonsImage
 * @param {THREE.Material} capMaterial
 * @returns {THREE.Mesh[]}
 */
export function buildWallMeshes(
  walls,
  doorsByWallId,
  windowsByWallId,
  center,
  metersPerPixel,
  materials,
  roomPolygonsImage,
  capMaterial,
) {
  const meshes = [];
  const yLevels = [
    0,
    WINDOW_SILL_HEIGHT,
    DOOR_HEIGHT,
    WINDOW_SILL_HEIGHT + WINDOW_HEIGHT,
    WALL_HEIGHT,
  ]
    .map((value) => Math.min(value, WALL_HEIGHT))
    .filter((value) => value >= 0);
  const uniqueLevels = [...new Set(yLevels.map((value) => Math.round(value * 1000) / 1000))].sort(
    (a, b) => a - b,
  );
  const yBands = [];
  for (let i = 0; i < uniqueLevels.length - 1; i += 1) {
    if (uniqueLevels[i + 1] - uniqueLevels[i] > 0.01) {
      yBands.push([uniqueLevels[i], uniqueLevels[i + 1]]);
    }
  }

  const shadeVariant = resolveWallShadeVariantFor3D();

  walls.forEach((wall, index) => {
    const wallId = typeof wall.id === 'string' ? wall.id : `wall_${index + 1}`;
    const start = parseImagePoint(wall.start);
    const end = parseImagePoint(wall.end);
    if (!start || !end) return;

    const wallThicknessMeters = Number(wall.thicknessMeters);
    const thickness =
      Number.isFinite(wallThicknessMeters) && wallThicknessMeters > 0
        ? wallThicknessMeters
        : WALL_THICKNESS;

    const wallStartWorld = imagePointToWorld(start, center, metersPerPixel);
    const wallEndWorld = imagePointToWorld(end, center, metersPerPixel);
    const wallLengthPx = Math.hypot(end[0] - start[0], end[1] - start[1]);
    if (wallLengthPx < 1) return;
    const wallLengthM = wallLengthPx * metersPerPixel;

    const toMeters = (projection) =>
      projection
        ? {
            startDist: projection.startDist * metersPerPixel,
            endDist: projection.endDist * metersPerPixel,
          }
        : null;

    const doorProjections = (doorsByWallId.get(wallId) || [])
      .map((door) => toMeters(projectOpeningOntoWall(door.opening, start, end)))
      .filter(Boolean);
    const windowProjections = (windowsByWallId.get(wallId) || [])
      .map((windowItem) => toMeters(projectOpeningOntoWall(windowItem.opening, start, end)))
      .filter(Boolean);

    for (const [yBottom, yTop] of yBands) {
      const blocked = blockedIntervalsForYBand(
        yBottom,
        yTop,
        doorProjections,
        windowProjections,
      );
      const mergedBlocked = mergeIntervals(blocked, wallLengthM);
      const solidIntervals = complementIntervals(mergedBlocked, wallLengthM);

      for (const [dist0, dist1] of solidIntervals) {
        const points = worldPointsAlongWall(
          wallStartWorld,
          wallEndWorld,
          wallLengthM,
          dist0,
          dist1,
        );
        const segmentMeshes = buildCenterlineWallSegmentMeshes({
          startWorld: points.start,
          endWorld: points.end,
          thickness,
          yBottom,
          yTop,
          materials,
          roomPolygonsImage,
          center,
          metersPerPixel,
          capMaterial,
          wallId,
          shadeVariant,
        });
        segmentMeshes.forEach((mesh) => {
          mesh.userData.wallId = wallId;
          meshes.push(mesh);
        });
      }
    }
  });

  return meshes;
}

/**
 * @param {Array<Record<string, unknown>>} doors
 * @param {Array<Record<string, unknown>>} windows
 * @param {{ x: number; y: number }} center
 * @param {number} metersPerPixel
 * @param {THREE.Material} doorMaterial
 * @param {THREE.Material} windowMaterial
 * @returns {THREE.Object3D[]}
 */
export function buildOpeningMarkers(
  doors,
  windows,
  center,
  metersPerPixel,
  doorMaterial,
  windowMaterial,
) {
  const markers = [];

  doors.forEach((door, index) => {
    const opening = door.opening;
    if (!Array.isArray(opening) || opening.length < 2) return;
    const p0 = parseImagePoint(opening[0]);
    const p1 = parseImagePoint(opening[1]);
    if (!p0 || !p1) return;
    const w0 = imagePointToWorld(p0, center, metersPerPixel);
    const w1 = imagePointToWorld(p1, center, metersPerPixel);
    const length = Math.hypot(w1.x - w0.x, w1.z - w0.z);
    if (length < 0.01) return;

    const mesh = createWallPiece(w0, w1, 0.01, 0.06, 0.04, doorMaterial);
    if (mesh) {
      mesh.userData.openingId = typeof door.id === 'string' ? door.id : `door_${index + 1}`;
      markers.push(mesh);
    }
  });

  windows.forEach((windowItem, index) => {
    const opening = windowItem.opening;
    if (!Array.isArray(opening) || opening.length < 2) return;
    const p0 = parseImagePoint(opening[0]);
    const p1 = parseImagePoint(opening[1]);
    if (!p0 || !p1) return;
    const w0 = imagePointToWorld(p0, center, metersPerPixel);
    const w1 = imagePointToWorld(p1, center, metersPerPixel);
    const mesh = createWallPiece(
      w0,
      w1,
      WINDOW_SILL_HEIGHT,
      WINDOW_SILL_HEIGHT + 0.06,
      0.04,
      windowMaterial,
    );
    if (mesh) {
      mesh.userData.openingId =
        typeof windowItem.id === 'string' ? windowItem.id : `window_${index + 1}`;
      markers.push(mesh);
    }
  });

  return markers;
}

/**
 * @param {Array<{ x: number; z: number }>} baseCornersXZ p1..p4
 * @param {number} wallHeightMeters
 * @param {THREE.Material} material
 * @param {number} [yBase]
 * @returns {THREE.Mesh | null}
 */
export function createWallPrismFromFootprint(baseCornersXZ, wallHeightMeters, material, yBase = 0) {
  if (!baseCornersXZ || baseCornersXZ.length < 4) return null;
  if (wallHeightMeters <= 0.01) return null;
  if (!isValidWorldXZQuad(baseCornersXZ)) return null;

  return createWallPrismFromPolygon(baseCornersXZ, wallHeightMeters, material, yBase);
}

/**
 * @param {Array<{ x: number; z: number }>} basePointsXZ
 * @param {number} heightMeters
 * @param {THREE.Material} material
 * @param {number} [yBase]
 * @returns {THREE.Mesh | null}
 */
export function createWallPrismFromPolygon(basePointsXZ, heightMeters, material, yBase = 0) {
  const n = basePointsXZ.length;
  if (n < 3 || heightMeters <= 0.01) return null;

  const yTop = yBase + heightMeters;
  const positions = /** @type {number[]} */ ([]);
  const normals = /** @type {number[]} */ ([]);
  const areaSign = Math.sign(polygonSignedAreaXZ(basePointsXZ)) || 1;

  const pushVertex = (point, y, normal) => {
    positions.push(point.x, y, point.z);
    normals.push(normal.x, normal.y, normal.z);
  };

  const pushTriangle = (a, b, c, y, normal) => {
    pushVertex(a, y, normal);
    pushVertex(b, y, normal);
    pushVertex(c, y, normal);
  };

  const pushSideTriangle = (a, b, c) => {
    const ab = { x: b.x - a.x, y: b.y - a.y, z: b.z - a.z };
    const ac = { x: c.x - a.x, y: c.y - a.y, z: c.z - a.z };
    const normalRaw = {
      x: ab.y * ac.z - ab.z * ac.y,
      y: ab.z * ac.x - ab.x * ac.z,
      z: ab.x * ac.y - ab.y * ac.x,
    };
    const len = Math.hypot(normalRaw.x, normalRaw.y, normalRaw.z) || 1;
    const normal = {
      x: normalRaw.x / len,
      y: normalRaw.y / len,
      z: normalRaw.z / len,
    };
    positions.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
    normals.push(
      normal.x, normal.y, normal.z,
      normal.x, normal.y, normal.z,
      normal.x, normal.y, normal.z,
    );
  };

  for (let i = 1; i < n - 1; i += 1) {
    if (areaSign > 0) {
      pushTriangle(basePointsXZ[0], basePointsXZ[i + 1], basePointsXZ[i], yTop, { x: 0, y: 1, z: 0 });
      pushTriangle(basePointsXZ[0], basePointsXZ[i], basePointsXZ[i + 1], yBase, { x: 0, y: -1, z: 0 });
    } else {
      pushTriangle(basePointsXZ[0], basePointsXZ[i], basePointsXZ[i + 1], yTop, { x: 0, y: 1, z: 0 });
      pushTriangle(basePointsXZ[0], basePointsXZ[i + 1], basePointsXZ[i], yBase, { x: 0, y: -1, z: 0 });
    }
  }

  for (let i = 0; i < n; i += 1) {
    const j = (i + 1) % n;
    const bottomA = { x: basePointsXZ[i].x, y: yBase, z: basePointsXZ[i].z };
    const bottomB = { x: basePointsXZ[j].x, y: yBase, z: basePointsXZ[j].z };
    const topA = { x: basePointsXZ[i].x, y: yTop, z: basePointsXZ[i].z };
    const topB = { x: basePointsXZ[j].x, y: yTop, z: basePointsXZ[j].z };
    if (areaSign > 0) {
      pushSideTriangle(bottomA, bottomB, topB);
      pushSideTriangle(bottomA, topB, topA);
    } else {
      pushSideTriangle(bottomA, topB, bottomB);
      pushSideTriangle(bottomA, topA, topB);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.computeBoundingSphere();
  geometry.computeBoundingBox();

  if (process.env.NODE_ENV === 'development') {
    const { minTopY, maxTopY, maxTopNormalError } = manualWallTopDebugMetrics(geometry);
    if (DEBUG_MANUAL_3D || maxTopY - minTopY > 1e-9 || maxTopNormalError > 1e-6) {
      console.debug('[Manual 3D] top cap debug', {
        minTopY,
        maxTopY,
        yRange: maxTopY - minTopY,
        maxTopNormalError,
        sharesTopSideVertices: false,
      });
    }
  }

  return new THREE.Mesh(geometry, material);
}

/**
 * @param {unknown} colorHex
 * @param {Map<string, THREE.MeshStandardMaterial>} cache
 * @returns {THREE.MeshStandardMaterial}
 */
function getManualWallMaterial(colorHex, cache) {
  const raw = typeof colorHex === 'string' ? colorHex.trim() : '';
  const hex = /^#[0-9A-Fa-f]{6}$/.test(raw) ? raw : WALL_COLOR;
  if (!cache.has(hex)) {
    cache.set(hex, createArchitecturalWallMaterial({ colorHex: hex }));
  }
  return cache.get(hex);
}

/**
 * @typedef {{ startDist: number; endDist: number; heightM: number; sillM: number }} ManualOpeningRange
 */

/**
 * @param {ManualOpeningRange[]} doors
 * @param {ManualOpeningRange[]} windows
 * @returns {Array<[number, number]>}
 */
function buildManualYBands(doors, windows) {
  /** @type {number[]} */
  const levels = [0, WALL_HEIGHT];

  for (const door of doors) {
    if (door.heightM > SEGMENT_EPS_METERS && door.heightM < WALL_HEIGHT) {
      levels.push(door.heightM);
    }
  }
  for (const win of windows) {
    if (win.sillM > SEGMENT_EPS_METERS && win.sillM < WALL_HEIGHT) {
      levels.push(win.sillM);
    }
    const top = win.sillM + win.heightM;
    if (top > SEGMENT_EPS_METERS && top < WALL_HEIGHT) {
      levels.push(top);
    }
  }

  const unique = [...new Set(levels.map((v) => Math.round(v * 1000) / 1000))].sort((a, b) => a - b);
  /** @type {Array<[number, number]>} */
  const bands = [];
  for (let i = 0; i < unique.length - 1; i += 1) {
    if (unique[i + 1] - unique[i] > SEGMENT_EPS_METERS) {
      bands.push([unique[i], unique[i + 1]]);
    }
  }
  return bands;
}

/**
 * @param {number} yBottom
 * @param {number} yTop
 * @param {ManualOpeningRange[]} doors
 * @param {ManualOpeningRange[]} windows
 * @returns {Array<[number, number]>}
 */
function manualBlockedIntervalsForYBand(yBottom, yTop, doors, windows) {
  /** @type {Array<[number, number]>} */
  const blocked = [];

  for (const door of doors) {
    if (yBottom < door.heightM - SEGMENT_EPS_METERS && yTop > SEGMENT_EPS_METERS) {
      blocked.push([door.startDist, door.endDist]);
    }
  }

  for (const win of windows) {
    const windowTop = win.sillM + win.heightM;
    if (yBottom < windowTop - SEGMENT_EPS_METERS && yTop > win.sillM + SEGMENT_EPS_METERS) {
      blocked.push([win.startDist, win.endDist]);
    }
  }

  return blocked;
}

export function buildWallPiecesWithOpenings(doors, windows, wallLengthM) {
  const yBands = buildManualYBands(doors, windows);
  return yBands.map(([yBottom, yTop]) => {
    const blocked = manualBlockedIntervalsForYBand(yBottom, yTop, doors, windows);
    const mergedBlocked = mergeIntervalsMeters(blocked, wallLengthM);
    return {
      yBottom,
      yTop,
      solidIntervals: complementIntervalsMeters(mergedBlocked, wallLengthM),
    };
  });
}

/**
 * @param {import('./floorPlanDesignerPivotWallPolygons.js').DesignerGraph | null} designerGraph
 * @param {{ joinPoints: Map<string, import('./floorPlanDesignerPivotWallPolygons.js').JoinPair | null>; roomJoinPoints: Map<string, import('./floorPlanDesignerPivotWallPolygons.js').JoinPair>; wallPivotJoins: Map<string, import('./floorPlanDesignerPivotWallPolygons.js').JoinPair>; bevelPivots: Set<string> } | null} joinCache
 * @param {string} wallId
 * @param {[number, number]} start
 * @param {[number, number]} end
 * @param {number} dist0M
 * @param {number} dist1M
 * @param {number} wallLengthM
 * @param {number} halfThicknessPx
 * @param {{ x: number; y: number }} center
 * @param {number} metersPerPixel
 * @param {number} yBottom
 * @param {number} yTop
 * @param {THREE.Material} material
 * @returns {THREE.Mesh | null}
 */
/**
 * @param {Point[]} corners2D
 * @param {{ x: number; y: number }} center
 * @param {number} metersPerPixel
 * @param {number} yBottom
 * @param {number} yTop
 * @param {import('./floorPlanThreeTextures.js').FloorPlanMaterialSet} materials
 * @param {Array<Array<[number, number]>>} roomPolygonsImage
 * @param {THREE.Material} capMaterial
 * @param {string} wallId
 * @param {{ x: number; z: number } | null} [wallAxis]
 * @returns {THREE.Mesh[]}
 */
function extrudeManualWallPolygon(
  corners2D,
  center,
  metersPerPixel,
  yBottom,
  yTop,
  materials,
  roomPolygonsImage,
  capMaterial,
  wallId,
  wallAxis = null,
  shadeVariant = 'normal',
  wallInsideMaterialConfig = null,
  wallOutsideMaterialConfig = null,
  textureBundle = null,
) {
  if (!isValidSimplePolygon(corners2D)) return [];
  const ordered =
    corners2D.length === 4 ? ensureConsistentQuadWinding(corners2D) : corners2D;
  const cornersXZ = footprintCornersToWorldXZ(ordered, center, metersPerPixel, imagePointToWorld);
  return buildTexturedWallPrismMeshes({
    basePointsXZ: cornersXZ,
    yBottom,
    yTop,
    wallAxis,
    materials,
    roomPolygonsImage,
    center,
    metersPerPixel,
    capMaterial,
    wallId,
    shadeVariant,
    wallInsideMaterialConfig,
    wallOutsideMaterialConfig,
    textureBundle,
  });
}

/**
 * @returns {THREE.Mesh[]}
 */
function buildManualWallPrismSegment(
  designerGraph,
  renderModel,
  wallId,
  start,
  end,
  dist0M,
  dist1M,
  wallLengthM,
  halfThicknessPx,
  center,
  metersPerPixel,
  yBottom,
  yTop,
  materials,
  roomPolygonsImage,
  capMaterial,
  shadeVariant = 'normal',
  wallInsideMaterialConfig = null,
  wallOutsideMaterialConfig = null,
  textureBundle = null,
) {
  /** @type {THREE.Mesh[]} */
  const meshes = [];
  if (dist1M - dist0M <= SEGMENT_EPS_METERS || wallLengthM <= SEGMENT_EPS_METERS) {
    return meshes;
  }

  const t0 = dist0M / wallLengthM;
  const t1 = dist1M / wallLengthM;
  const segStart = pointOnWallAtT(start, end, t0);
  const segEnd = pointOnWallAtT(start, end, t1);
  const pixelsPerMeter = metersPerPixel > 0 ? 1 / metersPerPixel : 100;
  const segStartWorld = imagePointToWorld(segStart, center, metersPerPixel);
  const segEndWorld = imagePointToWorld(segEnd, center, metersPerPixel);
  const wallAxis = {
    x: segEndWorld.x - segStartWorld.x,
    z: segEndWorld.z - segStartWorld.z,
  };

  if (designerGraph && renderModel) {
    const seg = buildWallSegmentGeometry(
      designerGraph,
      wallId,
      t0,
      t1,
      pixelsPerMeter,
      renderModel,
      0,
    );
    if (seg.body) {
      const bodyMeshes = extrudeManualWallPolygon(
        seg.body,
        center,
        metersPerPixel,
        yBottom,
        yTop,
        materials,
        roomPolygonsImage,
        capMaterial,
        wallId,
        wallAxis,
        shadeVariant,
        wallInsideMaterialConfig,
        wallOutsideMaterialConfig,
        textureBundle,
      );
      meshes.push(...bodyMeshes);
    }
    if (meshes.length > 0) return meshes;
  }

  const corners2D = wallFootprintCorners2D(segStart, segEnd, halfThicknessPx);
  const footprintMeshes = extrudeManualWallPolygon(
    corners2D,
    center,
    metersPerPixel,
    yBottom,
    yTop,
    materials,
    roomPolygonsImage,
    capMaterial,
    wallId,
    wallAxis,
    shadeVariant,
    wallInsideMaterialConfig,
    wallOutsideMaterialConfig,
    textureBundle,
  );
  meshes.push(...footprintMeshes);
  return meshes;
}

/**
 * @param {Array<Record<string, unknown>>} walls
 * @param {Array<Record<string, unknown>>} doors
 * @param {Array<Record<string, unknown>>} windows
 * @param {{ x: number; y: number }} center
 * @param {number} metersPerPixel
 * @param {import('./floorPlanThreeTextures.js').FloorPlanMaterialSet} materials
 * @param {Array<Array<[number, number]>>} roomPolygonsImage
 * @param {THREE.Material[]} sceneMaterials
 * @param {import('./floorPlanDesignerPivotWallPolygons.js').DesignerGraph | null} [designerGraph]
 * @returns {THREE.Mesh[]}
 */
export function buildManualDesignerWallMeshes(
  walls,
  doors,
  windows,
  center,
  metersPerPixel,
  materials,
  roomPolygonsImage,
  sceneMaterials,
  designerGraph = null,
  textureBundle = null,
) {
  const meshes = [];
  /** @type {Map<string, THREE.MeshStandardMaterial>} */
  const capMaterialCache = new Map();

  const getCapMaterial = (colorHex) => {
    const raw = typeof colorHex === 'string' ? colorHex.trim() : '';
    const hex = /^#[0-9A-Fa-f]{6}$/.test(raw) ? raw : WALL_COLOR;
    if (!capMaterialCache.has(hex)) {
      const mat = getManualWallMaterial(hex, capMaterialCache);
      sceneMaterials.push(mat);
    }
    return capMaterialCache.get(hex);
  };

  const pixelsPerMeter = metersPerPixel > 0 ? 1 / metersPerPixel : 100;
  const renderModel = designerGraph
    ? buildDynamicWallRenderModel(designerGraph, pixelsPerMeter)
    : null;
  const shadeVariant = resolveWallShadeVariantFor3D();

  /** @type {Map<string, Record<string, unknown>>} */
  const uniquePhysicalWalls = new Map();
  /** @type {Map<string, Set<string>>} */
  const logicalIdsByPhysicalKey = new Map();
  walls.forEach((wall, index) => {
    const wallId = typeof wall.id === 'string' ? wall.id : `wall_${index + 1}`;
    const physicalKey =
      typeof wall.physicalWallId === 'string' && wall.physicalWallId
        ? wall.physicalWallId
        : wallId;
    if (!uniquePhysicalWalls.has(physicalKey)) {
      uniquePhysicalWalls.set(physicalKey, wall);
    }
    if (!logicalIdsByPhysicalKey.has(physicalKey)) {
      logicalIdsByPhysicalKey.set(physicalKey, new Set());
    }
    logicalIdsByPhysicalKey.get(physicalKey).add(wallId);
  });

  /** @type {Map<string, ManualOpeningRange[]>} */
  const doorsByWallId = new Map();
  /** @type {Map<string, ManualOpeningRange[]>} */
  const windowsByWallId = new Map();

  uniquePhysicalWalls.forEach((wall, physicalKey) => {
    const wallId = typeof wall.id === 'string' ? wall.id : physicalKey;
    const start = parseImagePoint(wall.start);
    const end = parseImagePoint(wall.end);
    if (!start || !end) return;

    const wallLengthPx = Math.hypot(end[0] - start[0], end[1] - start[1]);
    const wallLengthM = wallLengthPx * metersPerPixel;
    if (wallLengthM < SEGMENT_EPS_METERS) return;

    const parseDoor = (opening) => {
      const range = openingHorizontalRangeMeters(
        opening,
        wallLengthM,
        DEFAULT_DOOR_WIDTH_METERS,
      );
      if (!range) return null;
      const heightM = Number(opening.heightMeters);
      return {
        startDist: range.startM,
        endDist: range.endM,
        heightM:
          Number.isFinite(heightM) && heightM > 0 ? Math.min(heightM, WALL_HEIGHT) : DOOR_HEIGHT,
        sillM: 0,
      };
    };

    const parseWindow = (opening) => {
      const range = openingHorizontalRangeMeters(
        opening,
        wallLengthM,
        DEFAULT_WINDOW_WIDTH_METERS,
      );
      if (!range) return null;
      const heightM = Number(opening.heightMeters);
      const sillM = Number(opening.sillHeightMeters);
      return {
        startDist: range.startM,
        endDist: range.endM,
        heightM:
          Number.isFinite(heightM) && heightM > 0
            ? Math.min(heightM, WALL_HEIGHT)
            : WINDOW_HEIGHT,
        sillM:
          Number.isFinite(sillM) && sillM >= 0
            ? Math.min(sillM, WALL_HEIGHT)
            : WINDOW_SILL_HEIGHT,
      };
    };

    const logicalIds = logicalIdsByPhysicalKey.get(physicalKey) || new Set([wallId]);
    doors
      .filter((d) => logicalIds.has(String(d.wallId)))
      .forEach((door) => {
        const parsed = parseDoor(door);
        if (!parsed) return;
        if (!doorsByWallId.has(physicalKey)) doorsByWallId.set(physicalKey, []);
        doorsByWallId.get(physicalKey).push(parsed);
      });

    windows
      .filter((w) => logicalIds.has(String(w.wallId)))
      .forEach((windowItem) => {
        const parsed = parseWindow(windowItem);
        if (!parsed) return;
        if (!windowsByWallId.has(physicalKey)) windowsByWallId.set(physicalKey, []);
        windowsByWallId.get(physicalKey).push(parsed);
      });
  });

  uniquePhysicalWalls.forEach((wall, physicalKey) => {
    const wallId = typeof wall.id === 'string' ? wall.id : physicalKey;
    const start = parseImagePoint(wall.start);
    const end = parseImagePoint(wall.end);
    if (!start || !end) return;

    const wallLengthPx = Math.hypot(end[0] - start[0], end[1] - start[1]);
    const wallLengthM = wallLengthPx * metersPerPixel;
    if (wallLengthM < SEGMENT_EPS_METERS) return;

    const thicknessMeters = Number(wall.thicknessMeters);
    const thickness =
      Number.isFinite(thicknessMeters) && thicknessMeters > 0
        ? thicknessMeters
        : MANUAL_WALL_THICKNESS_DEFAULT;
    const halfThicknessPx = thickness / metersPerPixel / 2;

    const wallDoors = doorsByWallId.get(physicalKey) || [];
    const wallWindows = windowsByWallId.get(physicalKey) || [];

    const capMaterial = getCapMaterial(wall.color);
    const wallInsideMaterialConfig = getWallMaterialConfig(
      /** @type {{ color?: string; material?: unknown; insideMaterial?: unknown }} */ (wall),
      'inside',
    );
    const wallOutsideMaterialConfig = getWallMaterialConfig(
      /** @type {{ color?: string; material?: unknown; outsideMaterial?: unknown }} */ (wall),
      'outside',
    );
    const wallPieces = buildWallPiecesWithOpenings(wallDoors, wallWindows, wallLengthM);

    for (const { yBottom, yTop, solidIntervals } of wallPieces) {
      for (const [dist0, dist1] of solidIntervals) {
        const segmentMeshes = buildManualWallPrismSegment(
          designerGraph,
          renderModel,
          wallId,
          start,
          end,
          dist0,
          dist1,
          wallLengthM,
          halfThicknessPx,
          center,
          metersPerPixel,
          yBottom,
          yTop,
          materials,
          roomPolygonsImage,
          capMaterial,
          shadeVariant,
          wallInsideMaterialConfig,
          wallOutsideMaterialConfig,
          textureBundle,
        );
        meshes.push(...segmentMeshes);
      }
    }
  });

  if (renderModel?.junctionClosurePolygons?.length) {
    const junctionCapMaterial = getCapMaterial(WALL_COLOR);
    for (const closure of renderModel.junctionClosurePolygons) {
      const closureMeshes = extrudeManualWallPolygon(
        closure.polygon,
        center,
        metersPerPixel,
        0,
        WALL_HEIGHT,
        materials,
        roomPolygonsImage,
        junctionCapMaterial,
        closure.ownerWallId,
        null,
        shadeVariant,
      );
      meshes.push(...closureMeshes);
    }
  }

  return meshes;
}

/**
 * @param {Record<string, unknown>} opening
 * @param {number} wallLengthM
 * @returns {number | null}
 */
function openingParamT(opening, wallLengthM = 0) {
  return resolveOpeningT(opening, wallLengthM);
}

/**
 * @param {Array<Record<string, unknown>>} walls
 * @param {Array<Record<string, unknown>>} doors
 * @param {Array<Record<string, unknown>>} windows
 * @param {{ x: number; y: number }} center
 * @param {number} metersPerPixel
 * @param {THREE.Material} doorMaterial
 * @param {THREE.Material} windowMaterial
 * @returns {THREE.Object3D[]}
 */
export function buildManualDesignerOpeningMarkers(
  walls,
  doors,
  windows,
  center,
  metersPerPixel,
  doorMaterial,
  windowMaterial,
) {
  const markers = [];
  const wallById = new Map();
  walls.forEach((wall, index) => {
    const id = typeof wall.id === 'string' ? wall.id : `wall_${index + 1}`;
    const thicknessMeters = Number(wall.thicknessMeters);
    wallById.set(id, {
      ...wall,
      thicknessMeters:
        Number.isFinite(thicknessMeters) && thicknessMeters > 0
          ? thicknessMeters
          : MANUAL_WALL_THICKNESS_DEFAULT,
    });
  });

  const addMarker = (opening, type, material, bottomY, topY) => {
    const wallId = typeof opening.wallId === 'string' ? opening.wallId : null;
    const wall = wallId ? wallById.get(wallId) : null;
    if (!wall) return;

    const start = parseImagePoint(wall.start);
    const end = parseImagePoint(wall.end);
    if (!start || !end) return;

    const wallLengthPx = Math.hypot(end[0] - start[0], end[1] - start[1]);
    const wallLengthM = wallLengthPx * metersPerPixel;
    const t = openingParamT(opening, wallLengthM);
    if (t == null) return;

    const widthMeters = Number(opening.widthMeters);
    if (!Number.isFinite(widthMeters) || widthMeters <= 0) return;

    const halfWidthPx = widthMeters / metersPerPixel / 2;
    const [spanStart, spanEnd] = openingSpanOnWall(start, end, t, halfWidthPx);
    const markerHalfPx = Math.max(2, halfWidthPx * 0.15);
    const corners2D = ensureConsistentQuadWinding(
      wallFootprintCorners2D(spanStart, spanEnd, markerHalfPx),
    );
    const cornersXZ = footprintCornersToWorldXZ(corners2D, center, metersPerPixel, imagePointToWorld);
    const height = topY - bottomY;
    const mesh = createWallPrismFromFootprint(cornersXZ, height, material, bottomY);
    if (!mesh) return;
    mesh.userData.openingId =
      typeof opening.id === 'string' ? opening.id : `${type}_${markers.length + 1}`;
    mesh.userData.openingType = type;
    markers.push(mesh);
  };

  doors.forEach((door) => {
    addMarker(door, 'door', doorMaterial, 0.01, 0.06);
  });

  windows.forEach((windowItem) => {
    addMarker(
      windowItem,
      'window',
      windowMaterial,
      WINDOW_SILL_HEIGHT,
      WINDOW_SILL_HEIGHT + 0.06,
    );
  });

  return markers;
}

/**
 * @param {Array<Record<string, unknown>>} walls
 * @param {{ x: number; y: number }} center
 * @param {number} metersPerPixel
 * @returns {THREE.Mesh[]}
 */
export function buildManualDesignerDebugHelpers(walls, center, metersPerPixel) {
  const helpers = [];
  const startMat = new THREE.MeshStandardMaterial({ color: '#16a34a' });
  const endMat = new THREE.MeshStandardMaterial({ color: '#dc2626' });

  walls.forEach((wall, index) => {
    const wallId = typeof wall.id === 'string' ? wall.id : `wall_${index + 1}`;
    const start = parseImagePoint(wall.start);
    const end = parseImagePoint(wall.end);
    if (!start || !end) return;

    const wStart = imagePointToWorld(start, center, metersPerPixel);
    const wEnd = imagePointToWorld(end, center, metersPerPixel);

    const startMesh = new THREE.Mesh(new THREE.SphereGeometry(0.06, 8, 8), startMat);
    startMesh.position.set(wStart.x, 0.08, wStart.z);
    startMesh.userData.debugRole = 'wallStart';
    startMesh.userData.wallId = wallId;
    helpers.push(startMesh);

    const endMesh = new THREE.Mesh(new THREE.SphereGeometry(0.06, 8, 8), endMat);
    endMesh.position.set(wEnd.x, 0.08, wEnd.z);
    endMesh.userData.debugRole = 'wallEnd';
    endMesh.userData.wallId = wallId;
    helpers.push(endMesh);
  });

  return helpers;
}

/**
 * @param {Record<string, unknown> | null | undefined} analysisJson
 * @param {number} imageWidth
 * @param {number} imageHeight
 * @param {{
 *   textures?: import('./floorPlanThreeTextures.js').BaseTextureBundle | null;
 *   showVerticalWallEdges?: boolean;
 *   showHorizontalWallEdges?: boolean;
 * }} [options]
 * @returns {ReturnType<typeof buildSelectedAreaSceneObjects>}
 */
export function buildManualDesignerSceneObjects(analysisJson, imageWidth, imageHeight, options = {}) {
  const edgeOptions = resolveWallEdgeOptions(options);
  const warnings = [];
  const { metersPerPixel, usedFallback, scaleConfidence } = getMetersPerPixel(analysisJson);

  if (usedFallback) {
    warnings.push(
      `No reliable scale in JSON; using fallback metersPerPixel=${FALLBACK_METERS_PER_PIXEL}.`,
    );
  }
  if (scaleConfidence === 'low') {
    warnings.push('Scale confidence is low — 3D dimensions are approximate.');
  }

  const targetSpace =
    analysisJson?.targetSpace && typeof analysisJson.targetSpace === 'object'
      ? /** @type {Record<string, unknown>} */ (analysisJson.targetSpace)
      : null;

  const polygonRaw = Array.isArray(targetSpace?.polygon) ? targetSpace.polygon : [];
  const polygon = polygonRaw.map((pt) => parseImagePoint(pt)).filter(Boolean);

  const walls = Array.isArray(analysisJson?.walls)
    ? analysisJson.walls.filter((w) => w && typeof w === 'object')
    : [];
  const rawDoors = Array.isArray(analysisJson?.doors)
    ? analysisJson.doors.filter((d) => d && typeof d === 'object')
    : [];
  const rawWindows = Array.isArray(analysisJson?.windows)
    ? analysisJson.windows.filter((w) => w && typeof w === 'object')
    : [];
  const objects = Array.isArray(analysisJson?.objects)
    ? analysisJson.objects.filter((object) => object && typeof object === 'object')
    : [];
  const objectOpenings = objects
    .filter((object) => object.type === 'door' || object.type === 'window')
    .map((object) => ({
      id: object.id,
      wallId: object.wallId,
      t: object.positionOnWall,
      centerRatio: object.positionOnWall,
      widthMeters: object.width,
      heightMeters: object.height,
      sillHeightMeters: object.type === 'window' ? object.sillHeight : 0,
      type: object.type === 'door' ? 'hinged' : undefined,
    }));
  const rawOpeningIds = new Set([...rawDoors, ...rawWindows].map((opening) => String(opening.id)));
  const doors = [
    ...rawDoors,
    ...objectOpenings.filter((opening) => opening.type === 'hinged' && !rawOpeningIds.has(String(opening.id))),
  ];
  const windows = [
    ...rawWindows,
    ...objectOpenings.filter((opening) => opening.type !== 'hinged' && !rawOpeningIds.has(String(opening.id))),
  ];

  const filtered3D = filterVisible3DWallsAndOpenings(walls, doors, windows);
  const visibleWalls = filtered3D.visibleWalls;
  const visibleDoors = filtered3D.doors;
  const visibleWindows = filtered3D.windows;
  const visibleObjects = objects.filter((object) => {
    if (object.type !== 'door' && object.type !== 'window') return true;
    return filtered3D.visibleWallIds.has(String(object.wallId));
  });

  if (visibleWalls.length === 0) {
    warnings.push('No walls array found — wall geometry was not created.');
  }

  let center = getPolygonCenter(polygon);
  const outlinePoints = [...polygon];
  walls.forEach((wall) => {
    const start = parseImagePoint(wall.start);
    const end = parseImagePoint(wall.end);
    if (start) outlinePoints.push(start);
    if (end) outlinePoints.push(end);
  });

  if (polygon.length < 3 && walls.length > 0) {
    center = getPolygonCenter(outlinePoints);
    if (polygon.length < 3) {
      warnings.push('Target polygon has fewer than 3 points — using wall bounds for floor.');
    }
  }

  const paddingPx = metersPerPixel > 0 ? 0.5 / metersPerPixel : 40;
  const floorPolygon =
    polygon.length >= 3
      ? polygon
      : outlinePoints.length >= 2
        ? buildBoundingFloorPolygon(outlinePoints, paddingPx)
        : [];

  const planMaterials = createFloorPlanMaterials(options.textures);
  const roomPolygonsImage = buildRoomPolygonsForClassification(analysisJson);
  /** @type {THREE.Material[]} */
  const sceneMaterials = [
    planMaterials.wallInside,
    planMaterials.wallOutside,
    planMaterials.floor,
    planMaterials.wallInsideFallback,
    planMaterials.wallOutsideFallback,
    planMaterials.floorFallback,
  ];
  const doorMaterial = new THREE.MeshStandardMaterial({ color: '#dc2626' });
  const windowMaterial = new THREE.MeshStandardMaterial({ color: '#2563eb' });
  sceneMaterials.push(doorMaterial, windowMaterial);

  const designerGraph = designerGraphFromAnalysisJson({
    ...analysisJson,
    walls: visibleWalls,
  });
  const exportedRooms = Array.isArray(analysisJson?.rooms)
    ? analysisJson.rooms.filter((room) => room && typeof room === 'object')
    : [];

  const wallMeshes = buildManualDesignerWallMeshes(
    visibleWalls,
    visibleDoors,
    visibleWindows,
    center,
    metersPerPixel,
    planMaterials,
    roomPolygonsImage,
    sceneMaterials,
    designerGraph,
    options.textures,
  );
  wallMeshes.forEach((mesh) => {
    if (edgeOptions.showVerticalWallEdges) {
      attachBlackEdges(mesh, edgeOptions, { surface: 'wall' });
    }
  });

  if (process.env.NODE_ENV === 'development') {
    console.debug('[Manual 3D] buildManualDesignerSceneObjects', {
      wallsCount: visibleWalls.length,
      roomsCount: exportedRooms.length,
      meshesCount: wallMeshes.length,
    });
  }

  const markers = buildManualDesignerOpeningMarkers(
    visibleWalls,
    visibleDoors,
    visibleWindows,
    center,
    metersPerPixel,
    doorMaterial,
    windowMaterial,
  );

  const debugHelpers = DEBUG_MANUAL_3D
    ? buildManualDesignerDebugHelpers(visibleWalls, center, metersPerPixel)
    : [];
  const wallById = new Map();
  visibleWalls.forEach((wall, index) => {
    const id = typeof wall.id === 'string' ? wall.id : `wall_${index + 1}`;
    const thicknessMeters = Number(wall.thicknessMeters);
    wallById.set(id, {
      ...wall,
      thicknessMeters:
        Number.isFinite(thicknessMeters) && thicknessMeters > 0
          ? thicknessMeters
          : MANUAL_WALL_THICKNESS_DEFAULT,
    });
  });
  const openingMeshes = buildOpeningMeshesFromRecords({
    doors: visibleDoors,
    windows: visibleWindows,
    walls: visibleWalls,
    objects: visibleObjects,
    center,
    metersPerPixel,
  });
  const furnitureMeshes = visibleObjects
    .filter((object) => object.type === 'furniture')
    .map((object) => createSceneObjectFromData(object, { wallById, center, metersPerPixel }))
    .filter(Boolean);
  const objectMeshes = [...openingMeshes, ...furnitureMeshes];

  /** @type {THREE.Object3D | null} */
  let floor = null;
  if (exportedRooms.length > 0) {
    const floorGroup = new THREE.Group();
    for (const room of exportedRooms) {
      if ((room.visibleIn3D ?? true) === false) continue;
      const polygonRaw = Array.isArray(room.polygon) ? room.polygon : [];
      const roomPolygonRaw = polygonRaw.map((pt) => parseImagePoint(pt)).filter(Boolean);
      if (roomPolygonRaw.length < 3) continue;
      const roomPolygon = ensureConsistentFloorWinding(roomPolygonRaw);
      if (!isValidSimplePolygon(roomPolygon)) {
        console.warn('[Manual 3D] skipped invalid room floor polygon', room);
        continue;
      }
      const roomMaterialConfig = getRoomFloorMaterialConfig({
        floorColor: typeof room.floorColor === 'string' ? room.floorColor : FLOOR_COLOR,
        floorMaterial: room.floorMaterial,
      });
      const roomMaterial = resolveFloorMaterial(planMaterials, options.textures, roomMaterialConfig);
      sceneMaterials.push(roomMaterial);
      const roomFloor = buildFloorFromPolygon(roomPolygon, center, metersPerPixel, roomMaterial);
      if (roomFloor) {
        if (edgeOptions.showHorizontalWallEdges) {
          attachBlackEdges(roomFloor, edgeOptions, { surface: 'floor' });
        }
        floorGroup.add(roomFloor);
      }
    }
    floor = floorGroup.children.length > 0 ? floorGroup : null;
  } else {
    const normalizedFloorPolygon =
      floorPolygon.length >= 3 ? ensureConsistentFloorWinding(floorPolygon) : floorPolygon;
    const floorMaterial = createRoomFloorMaterial(planMaterials, FLOOR_COLOR);
    if (!planMaterials.loaded) {
      sceneMaterials.push(floorMaterial);
    }
    floor =
      normalizedFloorPolygon.length >= 3 && isValidSimplePolygon(normalizedFloorPolygon)
        ? buildFloorFromPolygon(normalizedFloorPolygon, center, metersPerPixel, floorMaterial)
        : null;
    if (floorPolygon.length >= 3 && !floor) {
      console.warn('[Manual 3D] skipped invalid fallback floor polygon', floorPolygon);
    }
    if (floor && edgeOptions.showHorizontalWallEdges) {
      attachBlackEdges(floor, edgeOptions, { surface: 'floor' });
    }
  }

  const ceilingMaterial = createRoomCeilingMaterial();
  sceneMaterials.push(ceilingMaterial);
  const ceilings = buildRoomCeilingsGroup(
    exportedRooms,
    visibleWalls,
    center,
    metersPerPixel,
    ceilingMaterial,
  );

  const sceneRadius = Math.max(
    1,
    Math.max(imageWidth, imageHeight) * metersPerPixel * 0.6,
    WALL_HEIGHT * 1.5,
  );

  return {
    floor,
    ceilings,
    walls: wallMeshes,
    markers: [...markers, ...debugHelpers],
    objects: objectMeshes,
    warnings,
    center,
    metersPerPixel,
    usedFallback,
    scaleConfidence,
    sceneRadius,
    materials: sceneMaterials,
  };
}

/**
 * @param {Record<string, unknown> | null | undefined} analysisJson
 * @param {number} imageWidth
 * @param {number} imageHeight
 * @param {{
 *   textures?: import('./floorPlanThreeTextures.js').BaseTextureBundle | null;
 *   showVerticalWallEdges?: boolean;
 *   showHorizontalWallEdges?: boolean;
 * }} [options]
 * @returns {{
 *   floor: THREE.Mesh | null;
 *   walls: THREE.Mesh[];
 *   markers: THREE.Object3D[];
 *   warnings: string[];
 *   center: { x: number; y: number };
 *   metersPerPixel: number;
 *   usedFallback: boolean;
 *   scaleConfidence: string;
 *   sceneRadius: number;
 * }}
 */
export function buildSelectedAreaSceneObjects(analysisJson, imageWidth, imageHeight, options = {}) {
  if (analysisJson?.source === 'manualFloorPlanDesigner') {
    return buildManualDesignerSceneObjects(analysisJson, imageWidth, imageHeight, options);
  }

  const edgeOptions = resolveWallEdgeOptions(options);
  const warnings = [];
  const { metersPerPixel, usedFallback, scaleConfidence } = getMetersPerPixel(analysisJson);

  if (usedFallback) {
    warnings.push(
      `No reliable scale in JSON; using fallback metersPerPixel=${FALLBACK_METERS_PER_PIXEL}.`,
    );
  }
  if (scaleConfidence === 'low') {
    warnings.push('Scale confidence is low — 3D dimensions are approximate.');
  }

  const targetSpace =
    analysisJson?.targetSpace && typeof analysisJson.targetSpace === 'object'
      ? /** @type {Record<string, unknown>} */ (analysisJson.targetSpace)
      : null;

  const polygonRaw = Array.isArray(targetSpace?.polygon) ? targetSpace.polygon : [];
  const polygon = polygonRaw
    .map((pt) => parseImagePoint(pt))
    .filter(Boolean);

  const walls = Array.isArray(analysisJson?.walls)
    ? analysisJson.walls.filter((w) => w && typeof w === 'object')
    : [];
  const doors = Array.isArray(analysisJson?.doors)
    ? analysisJson.doors.filter((d) => d && typeof d === 'object')
    : [];
  const windows = Array.isArray(analysisJson?.windows)
    ? analysisJson.windows.filter((w) => w && typeof w === 'object')
    : [];
  const objects = Array.isArray(analysisJson?.objects)
    ? analysisJson.objects.filter((object) => object && typeof object === 'object')
    : [];

  const filtered3D = filterVisible3DWallsAndOpenings(walls, doors, windows);
  const visibleWalls = filtered3D.visibleWalls;
  const visibleDoors = filtered3D.doors;
  const visibleWindows = filtered3D.windows;
  const visibleObjects = objects.filter((object) => {
    if (object.type !== 'door' && object.type !== 'window') return true;
    return filtered3D.visibleWallIds.has(String(object.wallId));
  });

  if (walls.length === 0) {
    warnings.push('No walls array found — wall geometry was not created.');
  }

  let center = getPolygonCenter(polygon);
  const outlinePoints = [...polygon];
  walls.forEach((wall) => {
    const start = parseImagePoint(wall.start);
    const end = parseImagePoint(wall.end);
    if (start) outlinePoints.push(start);
    if (end) outlinePoints.push(end);
  });

  if (polygon.length < 3 && walls.length > 0) {
    center = getPolygonCenter(outlinePoints);
    if (polygon.length < 3) {
      warnings.push('Target polygon has fewer than 3 points — using wall bounds for floor.');
    }
  }

  const paddingPx =
    metersPerPixel > 0 ? 0.5 / metersPerPixel : 40;
  const floorPolygon =
    outlinePoints.length >= 2
      ? buildBoundingFloorPolygon(outlinePoints, paddingPx)
      : [];

  const planMaterials = createFloorPlanMaterials(options.textures);
  const roomPolygonsImage = buildRoomPolygonsForClassification(analysisJson);
  const exportedRooms = Array.isArray(analysisJson?.rooms)
    ? analysisJson.rooms.filter((room) => room && typeof room === 'object')
    : [];
  const capMaterial = createArchitecturalWallMaterial({ colorHex: WALL_COLOR });
  const doorMaterial = new THREE.MeshStandardMaterial({ color: '#dc2626' });
  const windowMaterial = new THREE.MeshStandardMaterial({ color: '#2563eb' });
  /** @type {THREE.Material[]} */
  const sceneMaterials = [
    planMaterials.wallInside,
    planMaterials.wallOutside,
    planMaterials.floor,
    planMaterials.wallInsideFallback,
    planMaterials.wallOutsideFallback,
    planMaterials.floorFallback,
    capMaterial,
    doorMaterial,
    windowMaterial,
  ];

  const { doorsByWallId, windowsByWallId, warnings: assignWarnings } = assignOpeningsToWalls(
    visibleWalls,
    visibleDoors,
    visibleWindows,
  );
  warnings.push(...assignWarnings);

  /** @type {THREE.Object3D | null} */
  let floor = null;
  if (exportedRooms.length > 0) {
    const floorGroup = new THREE.Group();
    for (const room of exportedRooms) {
      if ((room.visibleIn3D ?? true) === false) continue;
      const roomPolygonRaw = Array.isArray(room.polygon)
        ? room.polygon.map((pt) => parseImagePoint(pt)).filter(Boolean)
        : [];
      if (roomPolygonRaw.length < 3) continue;
      const roomPolygon = ensureConsistentFloorWinding(roomPolygonRaw);
      if (!isValidSimplePolygon(roomPolygon)) continue;
      const roomColor =
        typeof room.floorColor === 'string' && room.floorColor ? room.floorColor : FLOOR_COLOR;
      const roomMaterial = createRoomFloorMaterial(planMaterials, roomColor);
      if (!planMaterials.loaded) {
        sceneMaterials.push(roomMaterial);
      }
      const roomFloor = buildFloorFromPolygon(roomPolygon, center, metersPerPixel, roomMaterial);
      if (roomFloor) {
        if (edgeOptions.showHorizontalWallEdges) {
          attachBlackEdges(roomFloor, edgeOptions, { surface: 'floor' });
        }
        floorGroup.add(roomFloor);
      }
    }
    floor = floorGroup.children.length > 0 ? floorGroup : null;
  } else {
    const normalizedFloorPolygon =
      floorPolygon.length >= 3 ? ensureConsistentFloorWinding(floorPolygon) : floorPolygon;
    const floorMaterial = createRoomFloorMaterial(planMaterials, FLOOR_COLOR);
    if (!planMaterials.loaded) {
      sceneMaterials.push(floorMaterial);
    }
    floor =
      normalizedFloorPolygon.length >= 3 && isValidSimplePolygon(normalizedFloorPolygon)
        ? buildFloorFromPolygon(normalizedFloorPolygon, center, metersPerPixel, floorMaterial)
        : null;
    if (floor && edgeOptions.showHorizontalWallEdges) {
      attachBlackEdges(floor, edgeOptions, { surface: 'floor' });
    }
  }

  const ceilingMaterial = createRoomCeilingMaterial();
  sceneMaterials.push(ceilingMaterial);
  const ceilings = buildRoomCeilingsGroup(
    exportedRooms,
    visibleWalls,
    center,
    metersPerPixel,
    ceilingMaterial,
  );

  const wallMeshes = buildWallMeshes(
    visibleWalls,
    doorsByWallId,
    windowsByWallId,
    center,
    metersPerPixel,
    planMaterials,
    roomPolygonsImage,
    capMaterial,
  );
  wallMeshes.forEach((mesh) => {
    if (edgeOptions.showVerticalWallEdges) {
      attachBlackEdges(mesh, edgeOptions, { surface: 'wall' });
    }
  });

  const markers = buildOpeningMarkers(
    visibleDoors,
    visibleWindows,
    center,
    metersPerPixel,
    doorMaterial,
    windowMaterial,
  );

  const openingMeshes = buildOpeningMeshesFromRecords({
    doors: visibleDoors,
    windows: visibleWindows,
    walls: visibleWalls,
    objects: visibleObjects,
    center,
    metersPerPixel,
  });
  const wallById = new Map();
  visibleWalls.forEach((wall, index) => {
    const id = typeof wall.id === 'string' ? wall.id : `wall_${index + 1}`;
    wallById.set(id, wall);
  });
  const furnitureMeshes = visibleObjects
    .filter((object) => object.type === 'furniture')
    .map((object) => createSceneObjectFromData(object, { wallById, center, metersPerPixel }))
    .filter(Boolean);
  const objectMeshes = [...openingMeshes, ...furnitureMeshes];

  const sceneRadius = Math.max(
    1,
    Math.max(imageWidth, imageHeight) * metersPerPixel * 0.6,
    WALL_HEIGHT * 1.5,
  );

  return {
    floor,
    ceilings,
    walls: wallMeshes,
    markers,
    objects: objectMeshes,
    warnings,
    center,
    metersPerPixel,
    usedFallback,
    scaleConfidence,
    sceneRadius,
    materials: sceneMaterials,
  };
}

export const WALL_COLOR = '#f4f1e8';
export const FLOOR_COLOR = '#b8e6b0';
export const EDGE_COLOR = '#1a1a1a';

/**
 * @param {THREE.Mesh} mesh
 * @param {import('./floorPlanThreeVerticalEdges.js').WallEdgeOptions} [edgeOptions]
 * @param {{ surface?: 'wall' | 'floor' }} [context]
 * @returns {THREE.LineSegments | null}
 */
export function attachBlackEdges(mesh, edgeOptions, context = { surface: 'wall' }) {
  return attachMeshEdges(mesh, edgeOptions, context);
}

/**
 * @param {THREE.Object3D[]} objects
 * @param {THREE.Material[]} [materials]
 */
export function disposeSceneObjects(objects, materials = []) {
  if (materials.length > 0) {
    disposeFloorPlanMaterials(materials);
  }
  objects.forEach((obj) => {
    obj.traverse?.((child) => {
      if (child instanceof THREE.Mesh || child instanceof THREE.LineSegments) {
        child.geometry?.dispose?.();
        if (materials.length === 0) {
          if (Array.isArray(child.material)) {
            child.material.forEach((mat) => mat.dispose?.());
          } else {
            child.material?.dispose?.();
          }
        }
      }
    });
  });
}
